import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Highlighter from 'react-highlight-words';
import { Translate } from 'react-localize-redux';
import { Dialog, TextField, Button, Spinner } from '../../components/widgets';
import Icon, { IconSizes, IconVariants } from '../../components/foundations/Icon';
import { SHOW_ACCOUNT_SELECT_MODAL_EVENT } from '../consts';
import { impersonateOrg } from '../../services/organizations';
import { useOrganizations } from '../../services/organizationsHooks';
import { computeVisibleOrgs, isCurrentOrg, RESULTS_LIMIT } from '../accountSelectFilters';
import defaultUserIcon from '../../resources/png/default_user_icon.png';
import css from './AccountSelectDialog.css';

const PARTNERS = 'STATIC.PAGES.PARTNERS_PORTAL';
const KEY = `${PARTNERS}.ACCOUNT_SELECT_MODAL`;

const LISTBOX_ID = 'account-select-listbox';

const LOAD_MORE_THRESHOLD_PX = 120;

// Enter has an owner whenever one of these is focused, so the dialog must not steal it.
const INTERACTIVE_TARGET = 'button, a, [role="switch"], input[type="checkbox"]';

const rowDomId = orgId => `account-select-row-${orgId}`;

// autoEscape: account names and search terms carry regex metacharacters that would otherwise compile
// to an invalid RegExp and throw out of render.
function Match({ search, text, className }) {
    return (
        <span className={className}>
            <Highlighter
                autoEscape
                searchWords={[search]}
                textToHighlight={text || ''}
                highlightClassName={css.highlighted}
            />
        </span>
    );
}

Match.propTypes = {
    search: PropTypes.string.isRequired,
    text: PropTypes.string,
    className: PropTypes.string,
};

function AccountSelectDialog() {
    const { organizations: allOrgs, loading, loadFailed, load: loadOrgs, retry: retryLoad } = useOrganizations();
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [limit, setLimit] = useState(RESULTS_LIMIT);
    const [selectedId, setSelectedId] = useState(null);
    const [pending, setPending] = useState(false);
    const [accessFailedFor, setAccessFailedFor] = useState(null);

    const openRef = useRef(false);
    const headerRef = useRef(null);
    const listPaneRef = useRef(null);
    // One page per render pass: a single flick of the wheel fires dozens of scroll events.
    const growLockRef = useRef(false);
    // Index to select once its page has rendered, set when ↓ runs off the last row.
    const pendingIndexRef = useRef(null);
    const pendingRef = useRef(false);
    const inFlightRef = useRef(false);
    // Mirrors selectedId synchronously (both written through commitSelection) so two keydowns in one
    // tick do not each read the same pre-update selection.
    const selectedIdRef = useRef(null);

    const openDialog = useCallback(() => {
        setOpen(true);
        loadOrgs();
    }, [loadOrgs]);

    useEffect(() => {
        const onEvent = () => openDialog();
        window.addEventListener(SHOW_ACCOUNT_SELECT_MODAL_EVENT, onEvent);
        return () => window.removeEventListener(SHOW_ACCOUNT_SELECT_MODAL_EVENT, onEvent);
    }, [openDialog]);

    useEffect(() => {
        if (!open) {
            return undefined;
        }
        // Topbar is mounted outside the router, so the dialog would otherwise cover the next page.
        const onNavigate = () => setOpen(false);
        window.addEventListener('hashchange', onNavigate);
        window.addEventListener('popstate', onNavigate);
        return () => {
            window.removeEventListener('hashchange', onNavigate);
            window.removeEventListener('popstate', onNavigate);
        };
    }, [open]);

    const { options, total, allTotal, searching } = useMemo(
        () => computeVisibleOrgs({ allOrgs, searchString: search, limit }),
        [allOrgs, search, limit]
    );
    const hasMore = options.length < total;

    const loadMore = useCallback(() => {
        if (growLockRef.current) {
            return;
        }
        growLockRef.current = true;
        setLimit(prev => prev + RESULTS_LIMIT);
    }, []);

    useEffect(() => {
        growLockRef.current = false;
    }, [options]);

    const onListScroll = useCallback(
        event => {
            if (!hasMore) {
                return;
            }
            const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
            if (scrollHeight - scrollTop - clientHeight <= LOAD_MORE_THRESHOLD_PX) {
                loadMore();
            }
        },
        [hasMore, loadMore]
    );

    const commitSelection = useCallback(id => {
        selectedIdRef.current = id;
        setSelectedId(id);
        setAccessFailedFor(null);
    }, []);

    // scrollTop too: left deep-scrolled, the browser clamps it once the list shrinks back to one page,
    // which fires onListScroll at the new bottom and pages straight back in.
    useEffect(() => {
        commitSelection(null);
        pendingIndexRef.current = null;
        setLimit(RESULTS_LIMIT);
        if (listPaneRef.current) {
            listPaneRef.current.scrollTop = 0;
        }
    }, [open, search, commitSelection]);

    // Separate from the effect above, which already runs on every search change — clearing there loops.
    useEffect(() => {
        openRef.current = open;
        if (!open) {
            setSearch('');
        }
    }, [open]);

    // The field debounces by 300ms, so a trailing call after the close would land after the clear above
    // and resurrect the term on the next open.
    const onSearchChange = useCallback(term => {
        if (openRef.current) {
            setSearch(term);
        }
    }, []);

    const selected = useMemo(() => options.find(org => org.id === selectedId) || null, [options, selectedId]);

    // The row ↓ moved to only exists once the next page renders, so that move lands here instead.
    useEffect(() => {
        const index = pendingIndexRef.current;
        if (index === null || index >= options.length) {
            return;
        }
        pendingIndexRef.current = null;
        const next = options.slice(index).find(org => !isCurrentOrg(org));
        if (next) {
            commitSelection(next.id);
        }
    }, [options, commitSelection]);

    // A successful switch navigates away, so still pending on an open means it never happened — and the
    // dialog never unmounts to clear it. Closing does not cancel the POST, so a request that is still
    // in flight keeps its guard: two switches racing would leave the session in either account.
    useEffect(() => {
        if (open && !inFlightRef.current) {
            pendingRef.current = false;
            setPending(false);
        }
    }, [open]);

    useEffect(() => {
        if (selectedId !== null) {
            document.getElementById(rowDomId(selectedId))?.scrollIntoView({ block: 'nearest' });
        }
    }, [selectedId]);

    const selectOrg = useCallback(
        org => {
            if (isCurrentOrg(org)) {
                return;
            }
            commitSelection(org.id);
        },
        [commitSelection]
    );

    const accessAccount = useCallback(org => {
        if (!org || pendingRef.current || isCurrentOrg(org)) {
            return;
        }
        pendingRef.current = true;
        inFlightRef.current = true;
        setPending(true);
        setAccessFailedFor(null);
        impersonateOrg(org.id)
            .then(() => {
                inFlightRef.current = false;
                window.location.href = '/';
            })
            .catch(() => {
                inFlightRef.current = false;
                pendingRef.current = false;
                setPending(false);
                setAccessFailedFor(org.display_name);
            });
    }, []);

    const moveSelection = useCallback(
        step => {
            if (!options.length) {
                return;
            }
            const current = options.findIndex(org => org.id === selectedIdRef.current);
            let next = current + step;
            while (next >= 0 && next < options.length && isCurrentOrg(options[next])) {
                next += step;
            }
            if (next >= options.length) {
                if (hasMore) {
                    pendingIndexRef.current = options.length;
                    loadMore();
                }
                return;
            }
            if (next < 0) {
                return;
            }
            commitSelection(options[next].id);
        },
        [options, hasMore, loadMore, commitSelection]
    );

    // Reads the ref the arrows write, not the `selected` memo: Enter in the same tick as an arrow would
    // otherwise resolve against a selection React has not committed.
    const currentSelection = useCallback(() => options.find(org => org.id === selectedIdRef.current) || null, [
        options,
    ]);

    useEffect(() => {
        if (!open) {
            return undefined;
        }
        const onKeyDown = event => {
            // Scoped to the dialog: the surface is MUI's Paper, which we do not render, so we reach it
            // through our own header.
            const surface = headerRef.current?.parentNode;
            const { target } = event;
            if (!surface || !target || !target.nodeType || !surface.contains(target)) {
                return;
            }
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                moveSelection(1);
                return;
            }
            if (event.key === 'ArrowUp') {
                event.preventDefault();
                moveSelection(-1);
                return;
            }
            if (event.key !== 'Enter' || (target.closest && target.closest(INTERACTIVE_TARGET))) {
                return;
            }
            event.preventDefault();
            accessAccount(currentSelection());
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [open, moveSelection, currentSelection, accessAccount]);

    const close = useCallback(() => setOpen(false), []);

    const hasOrganizations = allOrgs.length > 0;

    const renderCount = () => {
        if (hasMore) {
            return (
                <Translate
                    id={`${KEY}.COUNT_SHOWING_FIRST`}
                    data={{ limit: options.length.toLocaleString(), total: total.toLocaleString() }}
                />
            );
        }
        if (searching) {
            return <Translate id={`${KEY}.COUNT_FOUND`} data={{ count: total.toLocaleString() }} />;
        }
        return (
            <Translate
                id={`${KEY}.COUNT_OF_TOTAL`}
                data={{ count: total.toLocaleString(), total: allTotal.toLocaleString() }}
            />
        );
    };

    const renderEmptyState = () => {
        if (searching) {
            return (
                <div className={css.empty}>
                    <div>
                        {/* renderInnerHtml is on globally (Main.jsx); interpolated data must stay text. */}
                        <Translate
                            id={`${KEY}.EMPTY_TITLE`}
                            data={{ query: search }}
                            options={{ renderInnerHtml: false }}
                        />
                    </div>
                    <div className={css.emptyHint}>
                        <Translate id={`${KEY}.EMPTY_HINT`} />
                    </div>
                </div>
            );
        }
        return (
            <div className={css.empty}>
                <Translate id={`${PARTNERS}.LANDING_PAGE_MODAL_EMPTY_ACCOUNTS`} />
            </div>
        );
    };

    return (
        <Dialog open={open} noPadding className={css.dialog} onEscapePress={close} onClose={close}>
            <>
                <header className={css.header} ref={headerRef}>
                    <h3 className={css.title}>
                        <Translate id={`${KEY}.TITLE`} />
                    </h3>
                    <button type="button" className={css.closeButton} aria-label="close" onClick={close}>
                        <Icon name="x" size={IconSizes.SMALL} variant={IconVariants.LIGHT} />
                    </button>
                </header>

                <div className={css.controls}>
                    <TextField
                        placeholder={`${PARTNERS}.LANDING_PAGE_MODAL_SEARCH_PLACEHOLDER`}
                        value={search}
                        debounce={300}
                        clearable
                        autoCompleteOff
                        inputConfig={{
                            autoFocus: true,
                            'aria-label': 'search',
                            'aria-controls': LISTBOX_ID,
                            'aria-activedescendant': selectedId === null ? undefined : rowDomId(selectedId),
                        }}
                        onChange={onSearchChange}
                    />
                </div>

                <div className={css.body}>
                    <div className={css.listPane} ref={listPaneRef} onScroll={onListScroll}>
                        {hasOrganizations ? (
                            <div className={css.listHeader}>
                                <span className={css.listHeaderLabel}>
                                    <Translate id={`${KEY}.${searching ? 'MATCHES' : 'ALL_ACCOUNTS'}`} />
                                </span>
                                <span className={css.listHeaderCount}>{renderCount()}</span>
                            </div>
                        ) : null}
                        {loading ? <Spinner show /> : null}
                        {loadFailed ? (
                            <div className={css.errorState}>
                                <div className={css.errorMessage}>
                                    <Translate id={`${KEY}.LOAD_ERROR`} />
                                </div>
                                <button type="button" className={css.retryButton} onClick={retryLoad}>
                                    <Translate id={`${KEY}.RETRY`} />
                                </button>
                            </div>
                        ) : null}
                        {!loading && !loadFailed && !options.length ? renderEmptyState() : null}
                        {/* Only with options, or the search input's aria-controls points at an empty one. */}
                        {options.length ? (
                            <div className={css.listOptions} role="listbox" id={LISTBOX_ID} aria-label="accounts">
                                {options.map(org => {
                                    const isCurrent = isCurrentOrg(org);
                                    const isSelected = selected ? selected.id === org.id : false;
                                    return (
                                        <div
                                            key={org.id}
                                            id={rowDomId(org.id)}
                                            className={classNames(css.row, {
                                                [css.rowSelected]: isSelected,
                                                [css.rowCurrent]: isCurrent,
                                            })}
                                            role="option"
                                            tabIndex={-1}
                                            aria-selected={isSelected}
                                            aria-disabled={isCurrent}
                                            onClick={() => selectOrg(org)}
                                            onDoubleClick={() => accessAccount(org)}
                                        >
                                            <img
                                                className={css.avatar}
                                                src={org.company_logo || defaultUserIcon}
                                                alt=""
                                            />
                                            <Match className={css.rowName} search={search} text={org.display_name} />
                                            {isCurrent ? (
                                                <span className={css.currentBadge}>
                                                    <Translate id={`${KEY}.CURRENT`} />
                                                </span>
                                            ) : null}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : null}
                        {hasMore ? (
                            <div className={css.listFooter}>
                                <button type="button" className={css.showMoreButton} onClick={loadMore}>
                                    <Translate
                                        id={`${KEY}.SHOW_MORE`}
                                        data={{
                                            count: Math.min(RESULTS_LIMIT, total - options.length).toLocaleString(),
                                        }}
                                    />
                                </button>
                            </div>
                        ) : null}
                    </div>
                </div>

                <div className={css.footer}>
                    {accessFailedFor ? (
                        <div className={css.errorBanner}>
                            {/* A cross-tenant display_name, and renderInnerHtml is on globally (Main.jsx). */}
                            <Translate
                                id={`${KEY}.ACCESS_ERROR`}
                                data={{ account: accessFailedFor }}
                                options={{ renderInnerHtml: false }}
                            />
                        </div>
                    ) : null}
                    <div className={css.footerRow}>
                        <div className={css.footerText}>
                            {selected ? (
                                <span className={css.footerAccount}>{selected.display_name}</span>
                            ) : (
                                <Translate id={`${KEY}.NO_SELECTION`} />
                            )}
                            <div className={css.footerHint}>
                                <Translate id={`${KEY}.KEYBOARD_HINT`} />
                            </div>
                        </div>
                        <Button
                            className={css.accessButton}
                            disabled={!selected || pending}
                            onClick={() => accessAccount(selected)}
                            text={pending ? `${KEY}.ACCESSING` : `${KEY}.ACCESS_ACCOUNT`}
                        />
                    </div>
                </div>
            </>
        </Dialog>
    );
}

export default AccountSelectDialog;
